import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOSchema from '../../components/SEOSchema';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://goautomat.com';

export default function About() {
  const { t } = useTranslation();
  
  useEffect(() => {
    document.title = 'Sobre a Automat | Nossa Missão e Valores | Automação com IA';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Conheça a Automat: nossa missão é democratizar a automação inteligente para empresas de todos os tamanhos. Descubra nossos valores e como transformamos o atendimento via WhatsApp com IA.');
    }
  }, []);

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "Sobre a Automat",
    "description": "Conheça a Automat: nossa missão é democratizar a automação inteligente para empresas de todos os tamanhos.",
    "url": `${siteUrl}/sobre`,
    "mainEntity": {
      "@type": "Organization",
      "name": "Automat",
      "url": siteUrl,
      "description": "Sistema de IA para automação de atendimento e vendas via WhatsApp",
      "foundingDate": "2024",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "São Paulo",
        "addressRegion": "SP",
        "addressCountry": "BR"
      }
    }
  };
  
  return (
    <div className="bg-gray-950 min-h-screen">
      <SEOSchema schema={aboutSchema} />
      {/* Navigation */}
      <nav className="bg-gray-900/50 backdrop-blur-sm border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center cursor-pointer">
              <img
                src="/assets/media/automat-logo.png"
                alt="Automat Logo" 
                className="h-10 w-auto object-contain"
              />
            </Link>
            <Link 
              to="/" 
              className="text-gray-400 hover:text-purple-400 transition-colors duration-300 cursor-pointer"
            >
              {t('nav.backToHome')}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              {t('about.title')}
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              {t('about.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{t('about.mission.title')}</h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                {t('about.mission.p1')}
              </p>
              <p className="text-gray-300 text-lg leading-relaxed">
                {t('about.mission.p2')}
              </p>
            </div>
            <div className="relative">
              <img
                src="/assets/media/about-team.jpg"
                alt="Time Automat"
                className="rounded-2xl w-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-16">{t('about.values.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-800/50 rounded-2xl p-8 border border-gray-700">
              <div className="w-14 h-14 flex items-center justify-center bg-purple-600/20 rounded-xl mb-6">
                <i className="ri-heart-line text-2xl text-purple-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-4">{t('about.values.passion.title')}</h3>
              <p className="text-gray-400">{t('about.values.passion.description')}</p>
            </div>
            <div className="bg-gray-800/50 rounded-2xl p-8 border border-gray-700">
              <div className="w-14 h-14 flex items-center justify-center bg-purple-600/20 rounded-xl mb-6">
                <i className="ri-lightbulb-line text-2xl text-purple-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-4">{t('about.values.innovation.title')}</h3>
              <p className="text-gray-400">{t('about.values.innovation.description')}</p>
            </div>
            <div className="bg-gray-800/50 rounded-2xl p-8 border border-gray-700">
              <div className="w-14 h-14 flex items-center justify-center bg-purple-600/20 rounded-xl mb-6">
                <i className="ri-team-line text-2xl text-purple-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-4">{t('about.values.collaboration.title')}</h3>
              <p className="text-gray-400">{t('about.values.collaboration.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-gray-500 text-sm">
            <p>{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
